"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";

/* Path dihitung dari app/edukasi-kesehatan/ - tempat berkas INI
   berada - bukan dari artikel/page.jsx yang merendernya. */
import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import { ARTIKEL_TERBARU } from "./artikel";
import MenuBagikan from "../../components/MenuBagikan";   

/* ============================================================
   DAFTAR ARTIKEL - /edukasi-kesehatan/artikel

   Susunannya mengikuti pola situs rumah sakit besar: judul besar,
   sebaris tapis kategori dengan kolom pencarian di kanannya, lalu
   satu sorotan di kiri dan daftar di kanan.

   Yang berubah dari versi sebelumnya:
     - Judul jauh lebih besar; eyebrow dan sub-judul dilepas supaya
       tapis kategori naik ke atas dan halaman punya satu titik masuk
       saja. (Mau mengembalikan sub-judul? Lihat catatan "SUB-JUDUL".)
     - Ada tapis kategori.
     - Kartu daftar memakai gambar besar, label kategori, dan nama
       penulis - bukan cuplikan dua baris.
     - Tombol bagikan di setiap gambar.

   ============================================================
   KATEGORI - bergantung pada medan `kategori` di artikel.js

   Setiap artikel boleh punya:  kategori: "berita"  atau  "artikel"
   Yang tidak punya dianggap "artikel". Jadi halaman ini tetap jalan
   tanpa mengubah artikel.js sama sekali - hanya saja pilnya cuma
   "Semua" dan "Artikel Kesehatan" sampai ada artikel berkategori
   lain.

   PIL_SELALU_TAMPIL mengatur pil untuk kategori yang belum ada
   isinya:
     true  -> semua pil tampil, termasuk yang kosong. Menekannya
              memunculkan keadaan "tidak ada hasil".
     false -> pil hanya muncul untuk kategori yang benar-benar ada
              artikelnya.

   Menambah kategori baru = tambahkan satu baris di KATEGORI di bawah,
   lalu pakai kuncinya di artikel.js.

   ============================================================
   TEKS BARU DITULIS DI SINI, BUKAN DI i18n

   Label kategori dan teks tombol bagikan sengaja ditaruh di berkas
   ini supaya halaman tidak menunggu berkas terjemahan diperbarui.
   Kalau nanti mau dipindahkan ke i18n, ganti KATEGORI/TEKS dengan
   panggilan t() - tidak ada yang lain yang perlu diubah.
   ============================================================ */

const LEBAR_HALAMAN = 1140;

/* Ubah ke false kalau tidak mau pil kategori kosong ikut tampil. */
const PIL_SELALU_TAMPIL = true;

/* Urutan di sini = urutan pil di layar. */
const KATEGORI = {
  berita: {
    pil: { id: "Berita", en: "News" },
    label: { id: "Berita", en: "News" },
  },
  artikel: {
    pil: { id: "Artikel Kesehatan", en: "Health Articles" },
    label: { id: "Artikel", en: "Article" },
  },
};

const TEKS = {
  semua: { id: "Semua", en: "All" },
  bagikan: { id: "Bagikan", en: "Share" },
  tersalin: { id: "Tautan disalin", en: "Link copied" },
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

function kategoriDari(artikel) {
  return KATEGORI[artikel.kategori] ? artikel.kategori : "artikel";
}

/* "Senin, 29 Jun 2026" - bulan disingkat supaya judul kartu tidak
   terdorong turun oleh tanggal yang panjang. */
function formatTanggal(tanggal, lang) {
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "id-ID", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Jakarta",
  }).format(new Date(tanggal));
}

export default function DaftarArtikelContent() {
  const { lang, t } = useLanguage();
  const [kata, setKata] = useState("");
  const [tapis, setTapis] = useState("semua");
  const [duaKolom, setDuaKolom] = useState(false);

  useEffect(() => {
    const ukur = () => setDuaKolom(window.innerWidth >= 1024);
    ukur();
    window.addEventListener("resize", ukur);
    return () => window.removeEventListener("resize", ukur);
  }, []);

  /* Dihitung sekali, bukan setiap ketikan - daftar artikel tidak
     berubah saat orang mengetik. Lihat PIL_SELALU_TAMPIL di atas. */
  const kategoriAda = useMemo(() => {
    if (PIL_SELALU_TAMPIL) return Object.keys(KATEGORI);
    const ada = new Set(ARTIKEL_TERBARU.map(kategoriDari));
    return Object.keys(KATEGORI).filter((k) => ada.has(k));
  }, []);

  const hasil = useMemo(() => {
    const q = kata.trim().toLowerCase();

    return ARTIKEL_TERBARU.filter((a) => {
      if (tapis !== "semua" && kategoriDari(a) !== tapis) return false;
      if (!q) return true;

      const teks = [pilihTeks(a.judul, lang), pilihTeks(a.ringkas, lang)]
        .join(" ")
        .toLowerCase();
      return teks.includes(q);
    });
  }, [kata, tapis, lang]);

  const sorotan = hasil[0] ?? null;
  const sisanya = hasil.slice(1);

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div style={{ maxWidth: 1600, margin: "0 auto", padding: "12px 16px" }}>
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("header.nav.home")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {t("daftarArtikel.judul")}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <section style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "44px 16px 64px" }}>

        {/* ---------- JUDUL ----------
           clamp: 30px di ponsel, 46px di layar lebar, tanpa media
           query. Judul sebesar ini yang membuat halaman terbaca
           sebagai bagian utama situs, bukan sub-halaman.

           SUB-JUDUL: kalau mau dikembalikan, sisipkan di sini
             <p className="text-[var(--c-body)]" style={{ marginTop: 14, maxWidth: 620, fontSize: 15.5, lineHeight: 1.7 }}>
               {t("daftarArtikel.sub")}
             </p> */}
        <h1
          className="text-balance font-bold leading-[1.08] tracking-tight text-[var(--c-ink)]"
          style={{ fontSize: "clamp(30px, 4.6vw, 46px)", marginBottom: 28 }}
        >
          {t("daftarArtikel.judul")}
        </h1>

        {/* ---------- TAPIS + PENCARIAN ----------
           Satu baris di layar lebar, menumpuk sendiri di ponsel lewat
           flex-wrap - tidak perlu JS. */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            marginBottom: kata.trim() ? 14 : 36,
          }}
        >
          {/* Pil disembunyikan kalau cuma ada satu kategori: pilihan
             tunggal bukan pilihan. */}
          {kategoriAda.length > 0 ? (
            <div role="group" aria-label={t("daftarArtikel.judul")} style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              <Pil aktif={tapis === "semua"} onClick={() => setTapis("semua")}>
                {pilihTeks(TEKS.semua, lang)}
              </Pil>

              {kategoriAda.map((k) => (
                <Pil key={k} aktif={tapis === k} onClick={() => setTapis(k)}>
                  {pilihTeks(KATEGORI[k].pil, lang)}
                </Pil>
              ))}
            </div>
          ) : (
            <span />
          )}

          {/* Label tersembunyi, bukan hanya placeholder - placeholder
             hilang saat orang mengetik dan tidak selalu dibacakan
             pembaca layar. */}
          <div style={{ position: "relative", width: "100%", maxWidth: 350 }}>
            <label htmlFor="cari-artikel" className="sr-only">
              {t("daftarArtikel.cari")}
            </label>

            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", width: 18, height: 18, color: "#94a3b8" }}
            >
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>

            <input
              id="cari-artikel"
              type="search"
              value={kata}
              onChange={(e) => setKata(e.target.value)}
              placeholder={t("daftarArtikel.cariPlaceholder")}
              className="text-[var(--c-ink)] placeholder:text-slate-400 focus:border-[var(--c-brand-mid)]"
              style={{
                width: "100%",
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                padding: "12px 14px 12px 44px",
                fontSize: 14.5,
                outline: "none",
              }}
            />
          </div>
        </div>

        {kata.trim() && (
          <p className="text-[var(--c-muted)]" style={{ marginBottom: 28, fontSize: 13 }}>
            {t("daftarArtikel.hasil", { n: hasil.length })}
          </p>
        )}

        {/* ---------- ISI ---------- */}
        {ARTIKEL_TERBARU.length === 0 ? (
          <Kosong
            judul={t("daftarArtikel.belumAda")}
            petunjuk={t("daftarArtikel.belumAdaPetunjuk")}
          />
        ) : hasil.length === 0 ? (
          <Kosong
            judul={t("daftarArtikel.kosong")}
            petunjuk={t("daftarArtikel.kosongPetunjuk")}
            aksi={
              <button
                type="button"
                onClick={() => {
                  setKata("");
                  setTapis("semua");
                }}
                className="font-bold text-[var(--c-brand)] underline underline-offset-4"
                style={{ marginTop: 12, fontSize: 14 }}
              >
                {t("daftarArtikel.bersihkan")}
              </button>
            }
          />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: duaKolom ? "minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
              gap: 36,
              alignItems: "start",
            }}
          >
            {sorotan && <Sorotan artikel={sorotan} lang={lang} />}

            <ul>
              {sisanya.map((a) => (
                <li
                  key={a.slug}
                  style={{ position: "relative", borderBottom: "1px solid #e2e8f0", padding: "20px 0" }}
                >
                  <Baris artikel={a} lang={lang} duaKolom={duaKolom} />
                </li>
              ))}

              {/* Kalau hasilnya cuma satu artikel, kolom kanan kosong.
                 Dibiarkan kosong lebih jujur daripada diisi kartu
                 ulangan - dan terisi sendiri begitu ada yang kedua. */}
            </ul>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

/* ============================================================
   SOROTAN - artikel terbaru, gambar besar
   ============================================================ */

function Sorotan({ artikel, lang }) {
  const href = `/edukasi-kesehatan/${artikel.slug}`;
  const judul = pilihTeks(artikel.judul, lang);
  const kat = kategoriDari(artikel);

  return (
    /* Tombol bagikan sengaja DI LUAR <Link>, bukan di dalamnya.
       Tombol di dalam tautan itu HTML yang tidak sah dan bikin
       navigasi papan ketik kacau; ditumpuk pakai position absolute
       hasilnya sama di mata, benar di struktur. */
    <article style={{ position: "relative" }}>
      <Link href={href} className="group block">
        <div
          style={{
            position: "relative",
            width: "100%",
            aspectRatio: "5 / 3",
            overflow: "hidden",
            borderRadius: 12,
            backgroundColor: "#f1faf5",
          }}
        >
          {artikel.gambar && (
            <Image
              src={artikel.gambar}
              alt={pilihTeks(artikel.gambarAlt, lang)}
              fill
              sizes="(min-width: 1024px) 550px, 100vw"
              priority
              className="transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transform-none"
              style={{ objectFit: "cover", objectPosition: "center" }}
            />
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18 }}>
          <Label kategori={kat} lang={lang} />
          <span className="text-[var(--c-muted)]" style={{ fontSize: 13.5 }}>
            {formatTanggal(artikel.tanggal, lang)}
          </span>
        </div>

        <h2
          className="font-bold leading-snug text-[var(--c-ink)] transition-colors group-hover:text-[var(--c-brand)]"
          style={{ marginTop: 12, fontSize: 25 }}
        >
          {judul}
        </h2>

        <p className="text-[var(--c-muted)]" style={{ marginTop: 10, fontSize: 13.5 }}>
          {pilihTeks(artikel.penulis, lang)}
        </p>

        <p className="text-[var(--c-body)]" style={{ marginTop: 10, fontSize: 15, lineHeight: 1.7 }}>
          {pilihTeks(artikel.ringkas, lang)}
        </p>
      </Link>

      {artikel.gambar && <TombolBagikan href={href} judul={judul} lang={lang} atas={12} kanan={12} />}
    </article>
  );
}

/* ============================================================
   BARIS - artikel lain, gambar di kanan
   ============================================================ */

function Baris({ artikel, lang, duaKolom }) {
  const href = `/edukasi-kesehatan/${artikel.slug}`;
  const judul = pilihTeks(artikel.judul, lang);
  const kat = kategoriDari(artikel);
  const lebarGambar = duaKolom ? 216 : 132;

  return (
    <>
      <Link href={href} className="group" style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Label kategori={kat} lang={lang} />
            <span className="text-[var(--c-muted)]" style={{ fontSize: 12.5 }}>
              {formatTanggal(artikel.tanggal, lang)}
            </span>
          </div>

          <h3
            className="font-bold leading-snug text-[var(--c-ink)] transition-colors group-hover:text-[var(--c-brand)]"
            style={{ marginTop: 9, fontSize: 16.5 }}
          >
            {judul}
          </h3>

          <p className="text-[var(--c-muted)]" style={{ marginTop: 8, fontSize: 12.5 }}>
            {pilihTeks(artikel.penulis, lang)}
          </p>
        </div>

        {artikel.gambar && (
          <div
            style={{
              position: "relative",
              flex: "0 0 auto",
              width: lebarGambar,
              aspectRatio: "5 / 3",
              overflow: "hidden",
              borderRadius: 10,
              backgroundColor: "#f1faf5",
            }}
          >
            <Image
              src={artikel.gambar}
              alt=""
              fill
              sizes={`${lebarGambar}px`}
              style={{ objectFit: "cover", objectPosition: "center" }}
            />
          </div>
        )}
      </Link>

      {artikel.gambar && <TombolBagikan href={href} judul={judul} lang={lang} atas={28} kanan={8} />}
    </>
  );
}

/* ============================================================
   LABEL KATEGORI
   ============================================================ */

function Label({ kategori, lang }) {
  return (
    <span
      className="font-semibold text-white"
      style={{
        backgroundColor: "#0f6a3b",
        borderRadius: 6,
        padding: "4px 10px",
        fontSize: 12,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      {pilihTeks(KATEGORI[kategori].label, lang)}
    </span>
  );
}

/* ============================================================
   TOMBOL BAGIKAN

   Di ponsel memakai lembar berbagi bawaan sistem. Di peramban meja
   yang tidak punya navigator.share, tautannya disalin ke papan klip
   dan tombolnya bilang begitu selama dua detik - kalau tidak, orang
   menekan dan tidak tahu apakah ada yang terjadi.
   ============================================================ */

function TombolBagikan({ href, judul, lang, atas, kanan }) {
  const [tersalin, setTersalin] = useState(false);

  useEffect(() => {
    if (!tersalin) return;
    const jeda = setTimeout(() => setTersalin(false), 2000);
    return () => clearTimeout(jeda);
  }, [tersalin]);

  async function bagikan(e) {
    e.preventDefault();
    e.stopPropagation();

    const url = `${window.location.origin}${href}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: judul, url });
        return;
      } catch {
        return; /* dibatalkan pengguna - bukan galat */
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setTersalin(true);
    } catch {
      /* papan klip diblokir (halaman non-HTTPS, izin ditolak).
         Diamkan - tidak ada yang bisa diperbuat dari sini. */
    }
  }

  return (
    <button
      type="button"
      onClick={bagikan}
      aria-label={`${pilihTeks(TEKS.bagikan, lang)}: ${judul}`}
      title={tersalin ? pilihTeks(TEKS.tersalin, lang) : pilihTeks(TEKS.bagikan, lang)}
      className="bg-white/95 text-[var(--c-brand)] shadow-sm backdrop-blur transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
      style={{
        position: "absolute",
        top: atas,
        right: kanan,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        borderRadius: 999,
        zIndex: 2,
      }}
    >
      {tersalin ? (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 16, height: 16 }}>
          <path d="m5 13 4 4L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 16, height: 16 }}>
          <circle cx="18" cy="5" r="2.6" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="6" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="18" cy="19" r="2.6" stroke="currentColor" strokeWidth="1.8" />
          <path d="M8.4 10.8 15.6 6.6M8.4 13.2l7.2 4.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      )}
    </button>
  );
}

/* ============================================================
   PIL TAPIS
   ============================================================ */

function Pil({ aktif, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={aktif}
      className="font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
      style={{
        borderRadius: 9,
        padding: "10px 18px",
        fontSize: 14.5,
        backgroundColor: aktif ? "#0f6a3b" : "#f1faf5",
        color: aktif ? "#ffffff" : "#0f6a3b",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function Kosong({ judul, petunjuk, aksi }) {
  return (
    <div
      style={{
        borderRadius: 14,
        border: "2px dashed #bfe3cf",
        backgroundColor: "#f1faf5",
        padding: "48px 20px",
        textAlign: "center",
      }}
    >
      <p className="font-bold text-[var(--c-ink)]" style={{ fontSize: 15 }}>
        {judul}
      </p>
      <p className="text-[var(--c-body)]" style={{ marginTop: 8, fontSize: 13.5 }}>
        {petunjuk}
      </p>
      {aksi}
    </div>
  );
}

function Pemisah() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
